import { useSyncExternalStore } from 'react';

/**
 * O RETORNO DE TODA CHAMADA — o toast que diz o que aconteceu (v2.1, p.32,
 * revisto em R04 de 05/10/2026).
 *
 * Antes disto, cada tela escolhia sozinha se avisava, e a maioria não avisava:
 * gravar com sucesso fechava a janela e nada mais, e quem clicou ficava sem
 * saber se o clique pegou. A regra mora na CAMADA DE CHAMADAS, e não nas
 * telas: **toda escrita avisa, e toda leitura que falha avisa**.
 *
 * ## O que a R04 mudou: o toast é completo
 *
 * "Todo toast diz o que foi feito, em qual registro, com quais valores e o que
 * isso mudou. A pessoa não precisa abrir a tela para saber se deu certo." Por
 * isso o retorno deixou de ser uma frase e virou a anatomia da p.32:
 *
 * 1. **tom** — verde fez, âmbar fez com ressalva, vermelho não fez, azul em
 *    andamento;
 * 2. **titulo** — objeto e particípio: "Abastecimento lançado";
 * 3. **alvo** — código e nome do registro afetado, nunca "o registro";
 * 4. **fatos** — os dois ou três valores gravados;
 * 5. **efeito** — o que mudou em outro lugar, com antes → depois;
 * 6. **registro** — código do lançamento, hora e autor;
 * 7. **acoes** — Ver abre o registro; Desfazer só enquanto o toast está na tela;
 * 8. **tempo** — 5 s para o sucesso, com a barra; mouse ou dedo pausa a conta.
 *
 * **Não existe texto padrão.** "Registro gravado.", "Salvo com sucesso!",
 * "Operação concluída." e "Erro ao salvar." são o que a página proíbe; toda
 * gravação declara o seu `titulo` (o `cliente-consulta.test.ts` reprova a que
 * não declara).
 *
 * ## Por que não é uma biblioteca
 *
 * Nenhuma dependência. `useSyncExternalStore` é do próprio React, e o
 * instantâneo é memorizado — devolver um array novo a cada leitura põe o React
 * num laço infinito de renderização, e esse defeito só aparece em produção.
 */

/** Verde fez, âmbar fez com ressalva, vermelho não fez, azul em andamento (p.32, 1). */
export type TomDoRetorno = 'sucesso' | 'ressalva' | 'falha' | 'andamento';

/** Em quê (p.32, 3): o código e o nome do registro afetado. */
export interface AlvoDoRetorno {
  readonly codigo?: string;
  readonly nome?: string;
}

/**
 * O que mudou além (p.32, 5). Com `antes` e `depois`, o toast desenha
 * "Saldo do Comboio 02 · 2.420 L → 2.100 L"; em texto, a frase como está.
 */
export type EfeitoDoRetorno =
  | string
  | { readonly rotulo: string; readonly antes?: string; readonly depois?: string };

/** Registro, hora e autor (p.32, 6). A hora é a do anúncio; o autor, quem está na sessão. */
export interface RegistroDoRetorno {
  readonly codigo?: string;
  readonly hora: string;
  readonly autor?: string;
}

/**
 * Uma ação do toast (p.32, 7). `rota` abre o registro ("Ver"); `aoTocar` faz
 * outra coisa ("Desfazer", no celular). O toast some depois de tocada.
 */
export interface AcaoDoRetorno {
  readonly rotulo: string;
  readonly rota?: string;
  readonly aoTocar?: () => void;
}

/**
 * O que UMA GRAVAÇÃO declara no `meta.retorno` da mutação — montado da
 * resposta do servidor e das variáveis que a tela mandou. Só o que a resposta
 * e as variáveis têm de verdade: sem efeito na resposta, sem efeito no toast.
 */
export interface RetornoDeclarado {
  /** O que foi feito: objeto e particípio. Obrigatório — não há texto padrão. */
  readonly titulo: string;
  /** `ressalva` quando fez, mas com algo a ver (âmbar). Sem isto, `sucesso`. */
  readonly tom?: 'sucesso' | 'ressalva';
  readonly alvo?: AlvoDoRetorno;
  /** De zero a três valores gravados, já formatados ("320 L", "4.832,0 h"). */
  readonly fatos?: readonly (string | false | null | undefined)[];
  readonly efeito?: EfeitoDoRetorno;
  /** O código do lançamento ("ABS-004812", "O.S. 000124"). */
  readonly codigo?: string;
  /** Rota que abre o registro — vira a ação "Ver". */
  readonly ver?: string;
}

/**
 * Monta o retorno de uma gravação. O `meta` do React Query é um só para todas
 * as mutações, e por isso não conhece o tipo da resposta nem das variáveis de
 * cada uma; `retornoDe` deixa quem declara escrever com os tipos de verdade.
 */
export type MontagemDoRetorno = (resposta: unknown, variaveis: unknown) => RetornoDeclarado;

export function retornoDe<R = unknown, V = unknown>(
  montar: (resposta: R, variaveis: V) => RetornoDeclarado,
): MontagemDoRetorno {
  return montar as MontagemDoRetorno;
}

/**
 * O que SÓ A TELA sabe e a resposta não traz: o código e o nome do registro
 * escolhido na lista, e o valor que a pessoa viu. Vai junto das variáveis da
 * mutação (nunca no corpo enviado ao servidor) para o retorno dizer "em quê".
 */
export interface ContextoDoRetorno {
  readonly alvo?: AlvoDoRetorno;
  readonly fatos?: readonly string[];
}

/** As variáveis sem o `contexto` — o que de fato vai ao servidor. */
export function semContexto<T extends { readonly contexto?: ContextoDoRetorno }>(variaveis: T): Omit<T, 'contexto'> {
  const copia: { contexto?: ContextoDoRetorno } & Omit<T, 'contexto'> = { ...variaveis };
  delete copia.contexto;
  return copia;
}

/** O que se anuncia. */
export interface NovoRetorno {
  readonly tom: TomDoRetorno;
  readonly titulo: string;
  readonly alvo?: AlvoDoRetorno;
  readonly fatos?: readonly string[];
  readonly efeito?: EfeitoDoRetorno;
  /** O código do lançamento; hora e autor o anúncio preenche. */
  readonly codigo?: string;
  /** Falha: o que fazer (p.32, "não fez"). */
  readonly comoResolver?: string;
  /** A linha de situação: "Nada foi gravado", "Sai sozinho quando a rede voltar". */
  readonly nota?: string;
  /** O número ao lado da nota: "3 na fila". */
  readonly contagem?: string;
  readonly acoes?: readonly AcaoDoRetorno[];
  /** Chamado quando o toast sai da tela, por qualquer caminho — o Desfazer da fila depende disto. */
  readonly aoSair?: () => void;
  /**
   * A falha de uma LEITURA da tela aberta: e da tela, e sai com ela
   * (`trocouDeTela`). Os robos no QAS (20/10/2026, item 12) acharam tres
   * falhas de leitura de /pessoas empilhadas sobre o painel da Biblioteca
   * tecnica, cobrindo as abas — a falha nao some sozinha, e a tela que a
   * causou ja tinha ido embora.
   */
  readonly daLeitura?: boolean;
}

export interface Retorno extends Omit<NovoRetorno, 'codigo'> {
  readonly id: string;
  readonly registro: RegistroDoRetorno;
  /** Quanto dura na tela; `null` para a falha, que espera a pessoa. */
  readonly duracaoMs: number | null;
}

/** Quanto tempo o retorno que não é falha fica na tela: 5 s (p.32, 8). A falha NÃO some sozinha. */
export const DURACAO_DO_RETORNO_MS = 5_000;

/** O teto de toasts ao mesmo tempo (p.31): além disso, o mais antigo sai. */
const TETO = 3;

let atuais: readonly Retorno[] = [];
const ouvintes = new Set<() => void>();
let semente = 0;

/* A contagem de cada toast: quanto falta e desde quando corre. Pausar guarda o
   que falta; retomar volta a contar dali. */
const relogios = new Map<string, { restante: number; desde: number; alca: ReturnType<typeof setTimeout> | null }>();

/* Quem está na sessão — quem assina o lançamento (p.32, 6). Quem sabe é a
   sessão (`modulos/sessao/memoria`), que avisa aqui; a camada de chamadas não
   importa módulo de tela. */
let autorAtual: string | undefined;

export function definirAutorDoRetorno(nome: string | null | undefined): void {
  autorAtual = nome ?? undefined;
}

function avisar() {
  for (const o of ouvintes) o();
}

function horaAgora(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function iniciarRelogio(id: string, ms: number) {
  const alca = setTimeout(() => {
    dispensar(id);
  }, ms);
  relogios.set(id, { restante: ms, desde: Date.now(), alca });
}

export function anunciar(novo: NovoRetorno): string {
  /* O MESMO aviso ja na tela nao entra de novo. Uma tela que le tres consultas
     da mesma permissao ausente empilhava tres toasts identicos — e o terceiro
     empurrava para fora o que talvez fosse outra coisa. Fica o que ja estava. */
  const igual = atuais.find(
    (r) =>
      r.tom === novo.tom &&
      r.titulo === novo.titulo &&
      r.comoResolver === novo.comoResolver &&
      r.alvo?.codigo === novo.alvo?.codigo &&
      (r.fatos ?? []).join('|') === (novo.fatos ?? []).join('|'),
  );
  if (igual) return igual.id;
  semente += 1;
  const id = `r${semente}`;
  const { codigo, ...resto } = novo;
  const duracaoMs = novo.tom === 'falha' ? null : DURACAO_DO_RETORNO_MS;
  const retorno: Retorno = {
    ...resto,
    ...(novo.fatos ? { fatos: novo.fatos.slice(0, 3) } : {}),
    id,
    registro: {
      ...(codigo === undefined ? {} : { codigo }),
      hora: horaAgora(),
      ...(autorAtual === undefined || novo.tom === 'falha' ? {} : { autor: autorAtual }),
    },
    duracaoMs,
  };
  const todos = [...atuais, retorno];
  const saem = todos.slice(0, Math.max(0, todos.length - TETO));
  atuais = todos.slice(-TETO);
  for (const r of saem) encerrar(r);
  avisar();
  if (duracaoMs !== null) iniciarRelogio(id, duracaoMs);
  return id;
}

/* O que acontece com todo toast que sai: o relógio para e quem pediu é avisado. */
function encerrar(r: Retorno) {
  const relogio = relogios.get(r.id);
  if (relogio?.alca) clearTimeout(relogio.alca);
  relogios.delete(r.id);
  r.aoSair?.();
}

export function dispensar(id: string): void {
  const saindo = atuais.find((r) => r.id === id);
  if (!saindo) return;
  atuais = atuais.filter((r) => r.id !== id);
  encerrar(saindo);
  avisar();
}

/**
 * Pausa a contagem (p.32, 8): o mouse em cima ou o dedo no toast. A falha não
 * conta, e pausar o que não conta não faz nada.
 */
export function pausarRetorno(id: string): void {
  const relogio = relogios.get(id);
  if (!relogio?.alca) return;
  clearTimeout(relogio.alca);
  relogios.set(id, { restante: Math.max(0, relogio.restante - (Date.now() - relogio.desde)), desde: Date.now(), alca: null });
}

/** Retoma a contagem de onde parou. */
export function retomarRetorno(id: string): void {
  const relogio = relogios.get(id);
  if (!relogio || relogio.alca) return;
  iniciarRelogio(id, relogio.restante);
}

/** Some com tudo. A troca de tela chama isto: retorno é do que a pessoa acabou de fazer. */
export function limparRetornos(): void {
  if (atuais.length === 0) return;
  const saem = atuais;
  atuais = [];
  for (const r of saem) encerrar(r);
  avisar();
}

/**
 * A troca de tela: saem as falhas de LEITURA da tela que ficou para trás. O
 * retorno de uma gravação fica — "Salvar" que navega para a lista ainda tem
 * de dizer o que gravou, e a falha da gravação espera a pessoa ler.
 */
export function trocouDeTela(): void {
  const saem = atuais.filter((r) => r.daLeitura === true);
  if (saem.length === 0) return;
  atuais = atuais.filter((r) => r.daLeitura !== true);
  for (const r of saem) encerrar(r);
  avisar();
}

/** Se o toast ainda está na tela — o Desfazer da fila só vale enquanto ele estiver. */
export function retornoNaTela(id: string): boolean {
  return atuais.some((r) => r.id === id);
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/* O instantâneo é o MESMO array até algo mudar. Devolver um array novo a cada
   leitura faz o React re-renderizar para sempre. */
const ler = () => atuais;

export function useRetornos(): readonly Retorno[] {
  return useSyncExternalStore(assinar, ler, ler);
}

/**
 * O que há agora, fora de componente.
 *
 * Existe para o teste, e é a MESMA leitura que o hook usa — não uma segunda
 * verdade. Escrever o teste contra uma cópia própria seria testar a cópia.
 */
export function retornosAgora(): readonly Retorno[] {
  return atuais;
}

/** Os fatos declarados, sem os vazios: `[cond && 'x', null]` vira `['x']`. */
export function fatosValidos(fatos: RetornoDeclarado['fatos']): string[] {
  return (fatos ?? []).filter((f): f is string => typeof f === 'string' && f.trim() !== '').slice(0, 3);
}
